import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import RefreshModal from 'legacyfrontend/components/home/components/RefreshModal';
import { HotelOfferCard } from './HotelOfferCard';
import { HotelFilters } from './HotelFilters';
import { MobileHotelFilters } from './MobileHotelFilters';
import {
  applyHotelFilters,
  applyHotelSort,
  computeHotelTypeCounts,
  computeMealCounts,
  hasActiveFilters,
  hasPromotionsAvailable,
} from '../utils/hotelFilterUtils';
import { calculateNights } from '../utils/nightsCalculation';
import { getMealOptions, getHotelTypeOptions } from '../constants/hotelMasterData';
import type {
  HotelOfferCardModel,
  HotelSearchStatus,
  HotelFilterState,
} from '../types/hotels.presentation';

const RefreshModalComponent = RefreshModal as React.ComponentType<{
  open: boolean;
  onConfirm: () => void;
  title?: string;
  message?: string;
  detail?: string;
  confirmLabel?: string;
  secondaryLabel?: string;
  onSecondary?: () => void;
  disabled?: boolean;
}>;

interface HotelResultsListProps {
  status: HotelSearchStatus;
  offers: HotelOfferCardModel[];
  message?: string;
  onRetry: () => void;
  checkIn?: string;
  checkOut?: string;
  adults?: number;
  childrenCount?: number;
  /** Timestamp (ms) when current results expire. The component shows a refresh dialog after expiry. */
  expiresAt?: number;
  /** Navigate to the base hotels search page. Shown as secondary action in the error modal. */
  onNavigateHome?: () => void;
}

const SKELETON_CARD_COUNT = 8;

function HotelOfferCardSkeleton() {
  return (
    <article
      aria-hidden='true'
      className='flex h-full min-h-[360px] animate-pulse flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm'
    >
      <div className='aspect-[16/10] w-full bg-gray-100' />

      <div className='flex flex-1 flex-col p-4'>
        <div className='flex flex-1 flex-col gap-4'>
          <div className='min-w-0 flex-1 space-y-3'>
            <div className='h-5 w-3/4 rounded bg-gray-200' />
            <div className='h-4 w-full rounded bg-gray-100' />
            <div className='h-4 w-5/6 rounded bg-gray-100' />
            <div className='h-4 w-1/2 rounded bg-gray-100' />
          </div>

          <div className='mt-auto space-y-3 border-t border-gray-100 pt-4'>
            <div className='h-3 w-24 rounded bg-gray-100' />
            <div className='flex items-center justify-between gap-4'>
              <div className='h-6 w-28 rounded bg-gray-200' />
              <div className='h-9 w-20 rounded bg-gray-200' />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function HotelEmptyResultsCard() {
  const { t } = useTranslation();

  return (
    <article className='rounded-xl border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-slate-600 shadow-sm'>
      <p role='status'>{t('hotels.results.empty')}</p>
    </article>
  );
}

export function HotelResultsList({
  status,
  offers,
  message,
  onRetry,
  checkIn,
  checkOut,
  adults,
  childrenCount,
  expiresAt,
  onNavigateHome,
}: HotelResultsListProps) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<HotelFilterState>({ sortBy: 'price-asc' });
  const isDesktop = useMediaQuery('(min-width:1024px)');
  const [showRefreshModal, setShowRefreshModal] = useState(false);

  const nights = useMemo(() => calculateNights(checkIn, checkOut), [checkIn, checkOut]);

  // Refresh timer: show dialog when current results expire
  useEffect(() => {
    setShowRefreshModal(false);

    if (expiresAt == null) return;

    const delay = Math.max(0, expiresAt - Date.now());
    const timer = setTimeout(() => {
      setShowRefreshModal(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [expiresAt]);

  const handleRefreshConfirm = useCallback(() => {
    setShowRefreshModal(false);
    onRetry();
  }, [onRetry]);

  const handleFiltersChange = useCallback((nextFilters: HotelFilterState) => {
    setFilters(nextFilters);
  }, []);

  // Static filter options (BM_MEALS + BM_HOTEL_TYPES) — not derived from results.
  const mealOptions = useMemo(() => getMealOptions(), []);
  const hotelTypeOptions = useMemo(() => getHotelTypeOptions(), []);

  const priceBounds = useMemo<[number, number]>(() => {
    if (offers.length === 0) return [0, 1000];

    const prices = offers.map((offer) => offer.totalPrice);
    return [Math.min(...prices), Math.max(...prices)];
  }, [offers]);

  const mealCounts = useMemo(() => computeMealCounts(offers, filters), [offers, filters]);

  const hotelTypeCounts = useMemo(() => computeHotelTypeCounts(offers, filters), [offers, filters]);

  const promotionsAvailable = useMemo(() => hasPromotionsAvailable(offers), [offers]);

  const filteredOffers = useMemo(() => {
    const base = hasActiveFilters(filters) ? applyHotelFilters(offers, filters) : offers;
    return applyHotelSort(base, filters.sortBy);
  }, [offers, filters]);

  if (status === 'idle') {
    return <p className='text-sm text-slate-600'>{t('hotels.results.idle')}</p>;
  }

  const isLoading = status === 'loading';
  const isStreaming = status === 'streaming';
  const isError = status === 'error';
  const isPartialError = status === 'partial-error';
  const isEmpty = status === 'empty';

  return (
    <>
      <div className='flex flex-col gap-3 lg:flex-row lg:gap-6'>
        {isDesktop ? (
          <HotelFilters
            filters={filters}
            hasPromotionsAvailable={promotionsAvailable}
            hotelTypeCounts={hotelTypeCounts}
            hotelTypeOptions={hotelTypeOptions}
            mealCounts={mealCounts}
            mealOptions={mealOptions}
            onFiltersChange={handleFiltersChange}
            priceBounds={priceBounds}
          />
        ) : (
          <MobileHotelFilters
            filters={filters}
            hasPromotionsAvailable={promotionsAvailable}
            hotelTypeCounts={hotelTypeCounts}
            hotelTypeOptions={hotelTypeOptions}
            mealCounts={mealCounts}
            mealOptions={mealOptions}
            onFiltersChange={handleFiltersChange}
            priceBounds={priceBounds}
          />
        )}

        <div className='min-w-0 flex-1 pb-20 lg:pb-0'>
          {!isEmpty && !isError ? (
            <div className='mb-3 flex min-h-5 flex-wrap items-center gap-2'>
              {isLoading ? (
                <p className='text-sm font-medium leading-5 text-gray-700' role='status'>
                  {t('hotels.results.loading')}
                </p>
              ) : filteredOffers.length < offers.length ? (
                <p className='text-sm leading-5 text-gray-500' role='status'>
                  {t('hotels.filters.showingFiltered', {
                    filtered: filteredOffers.length,
                    total: offers.length,
                  })}
                </p>
              ) : (
                <p className='text-sm font-medium leading-5 text-gray-700' role='status'>
                  {t('hotels.results.summary', {
                    count: filteredOffers.length,
                  })}
                </p>
              )}
              {!isLoading && nights != null ? (
                <span className='text-sm leading-5 text-gray-500'>
                  · {nights} {t(nights === 1 ? 'hotels.night' : 'hotels.nights')}
                </span>
              ) : null}
              {isStreaming ? (
                <span className='inline-flex h-5 items-center gap-1.5 text-sm font-medium leading-5 text-gray-500'>
                  <span
                    aria-hidden='true'
                    className='h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600'
                  />
                  {t('hotels.results.updating')}
                </span>
              ) : null}
            </div>
          ) : null}

          {isError || isPartialError ? (
            <div className='space-y-4'>
              <div
                className={`rounded-xl border p-4 ${
                  isPartialError
                    ? 'border-amber-200 bg-amber-50 text-amber-800'
                    : 'border-amber-200 bg-amber-50 text-amber-800'
                }`}
                role='alert'
              >
                <p>{t(message ?? 'hotels.results.searchError')}</p>
                <button className='btn btn-sm mt-3' onClick={onRetry} type='button'>
                  {t('hotels.results.retry')}
                </button>
              </div>
              {isPartialError && filteredOffers.length > 0 ? (
                <ul
                  aria-label={t('hotels.results.listLabel')}
                  className='grid grid-cols-1 gap-4 md:grid-cols-2 min-[1180px]:grid-cols-3 xl:grid-cols-4'
                >
                  {filteredOffers.map((offer) => (
                    <li className='h-full' key={`${offer.supplier}-${offer.hotelId}`}>
                      <HotelOfferCard
                        adults={adults}
                        checkIn={checkIn}
                        checkOut={checkOut}
                        childrenCount={childrenCount}
                        offer={offer}
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : isLoading ? (
            <ul
              aria-label={t('hotels.results.listLabel')}
              className='grid grid-cols-1 gap-4 md:grid-cols-2 min-[1180px]:grid-cols-3 xl:grid-cols-4'
            >
              {Array.from({ length: SKELETON_CARD_COUNT }).map((_, index) => (
                <li className='h-full' key={`hotel-result-skeleton-${index + 1}`}>
                  <HotelOfferCardSkeleton />
                </li>
              ))}
            </ul>
          ) : isEmpty ? (
            <ul
              aria-label={t('hotels.results.listLabel')}
              className='grid grid-cols-1 gap-4 md:grid-cols-2 min-[1180px]:grid-cols-3 xl:grid-cols-4'
            >
              <li className='h-full md:col-span-2 min-[1180px]:col-span-3 xl:col-span-4'>
                <HotelEmptyResultsCard />
              </li>
            </ul>
          ) : filteredOffers.length === 0 ? (
            <p className='rounded-xl bg-slate-100 p-4 text-sm text-slate-600' role='status'>
              {t('hotels.filters.noResults')}
            </p>
          ) : (
            <ul
              aria-label={t('hotels.results.listLabel')}
              className='grid grid-cols-1 gap-4 md:grid-cols-2 min-[1180px]:grid-cols-3 xl:grid-cols-4'
            >
              {filteredOffers.map((offer) => (
                <li className='h-full' key={`${offer.supplier}-${offer.hotelId}`}>
                  <HotelOfferCard
                    adults={adults}
                    checkIn={checkIn}
                    checkOut={checkOut}
                    childrenCount={childrenCount}
                    offer={offer}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <RefreshModalComponent
        confirmLabel={t('hotels.results.refreshModal.confirmLabel')}
        detail={t('hotels.results.refreshModal.detail')}
        message={t('hotels.results.refreshModal.message')}
        onConfirm={handleRefreshConfirm}
        open={showRefreshModal && status === 'success'}
        title={t('hotels.results.refreshModal.title')}
      />
      <RefreshModalComponent
        confirmLabel={t('hotels.results.errorModal.confirmLabel')}
        detail={t('hotels.results.errorModal.detail')}
        disabled={false}
        message={t('hotels.results.errorModal.message')}
        onConfirm={handleRefreshConfirm}
        onSecondary={onNavigateHome}
        open={isError || isPartialError}
        secondaryLabel={onNavigateHome ? t('hotels.results.errorModal.secondaryLabel') : undefined}
        title={t('hotels.results.errorModal.title')}
      />
    </>
  );
}
